import type { RenderParams } from '@seek/forms-ui';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Badge,
  Button,
  Box,
  Dropdown,
  IconSubCategory,
  Text,
  TextField,
  Textarea,
  Spread,
} from 'braid-design-system';
import React, { useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { TextLink } from 'src/components';
import { TRACKING_CODES, RECAPTCHA_KEY } from 'src/shared/constant';
import { logHandler } from 'src/shared/helpers';
import { useConfig } from 'src/shared/hooks/';
import { useTracking } from 'src/shared/hooks/useTracking';
import type { SuggestionType } from 'src/shared/types';
import { brandLookup } from 'src/utils/brandLookup';
import { useAccountIds } from 'src/utils/getAdvertiserId';

import translations from '../../../../.vocab';
import { type DynamicFormValues, useField } from '../../EnquiryForm';
import ArticleSuggest from '../ArticleSuggest/ArticleSuggest';
import { ContactNumber } from '../ContactNumber/ContactNumber';
import {
  useSubjects,
  type SubItem,
  type Reason,
} from '../SubjectData/useSubjects';

import { RenderField } from './renderField';
import { useFields } from './useFields';

import * as styles from './Form.css';

interface FormProps {
  querystring: boolean;
  handleSubmit: (event: any) => any;
  setFieldValue: RenderParams<DynamicFormValues>['setFieldValue'];
  setSubmitted: (submitted: boolean) => void;
  resetForm: () => void;
  showLegalPolicies?: boolean;
}

const createTextLink = (href: string) => (children: React.ReactNode) => (
  <TextLink
    // @ts-ignore
    href={href}
    tracking={TRACKING_CODES.content_element_pressed}
    trackingProps={{ elementDesign: 'link' }}
    weight="weak"
  >
    {children}
  </TextLink>
);

const Form = ({
  querystring,
  handleSubmit,
  setFieldValue,
  setSubmitted,
  resetForm,
  showLegalPolicies,
}: FormProps) => {
  const [invalidResponse, setInvalidResponse] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasonList, setReasonList] = useState<Reason[]>([]);
  const [ArticleSuggestion, setArticleSuggestion] = useState<
    SuggestionType | undefined
  >(undefined);
  const [recaptcha, setRecaptcha] = useState('');
  const [subItems, setSubItems] = useState<SubItem[] | undefined>(undefined);
  const { userId, billingId } = useAccountIds();
  const { t } = useTranslations(translations);

  const { environment, language, locale, site, rspEnabled } = useConfig();
  const brand = brandLookup(site);
  const { trackEvent } = useTracking();
  const postUrl =
    environment === 'production'
      ? 'https://wpt-contact-api.cloud.seek.com.au/talent'
      : 'https://wpt-contact-api.staging.cloud.seek.com.au/talent';

  const { subjectData } = useSubjects(site);

  const isANZ = site === 'employer-seek-au' || site === 'employer-seek-nz';

  const candidateUrl =
    site === 'employer-seek-au'
      ? 'https://www.seek.com.au'
      : 'https://www.seek.co.nz';

  const onSubmit = handleSubmit((formValues: any) => {
    if (!recaptcha && environment !== 'development') {
      return;
    }
    const userAgent = navigator.userAgent.toString();

    /* eslint-disable prefer-const */
    let {
      additionalInformation,
      rspEmail,
      rspName,
      rspPhone,
      rspActions,
      rspRelatedProducts,
      ...otherValues
    } = formValues;
    /* eslint-enable prefer-const */

    const rspRelatedProductsList =
      typeof rspRelatedProducts !== 'undefined'
        ? Object.keys(rspRelatedProducts)
        : [];
    const rspActionsList =
      typeof rspActions !== 'undefined' ? Object.keys(rspActions) : [];

    additionalInformation = `${additionalInformation}
      ${
        rspRelatedProductsList.length > 0
          ? `RSP Related Products: ${rspRelatedProductsList.toString()}`
          : ''
      }
      ${
        rspActionsList.length > 0
          ? `RSP Actions taken: ${rspActionsList.toString()}\nRSP contact: ${rspName}, e: ${rspEmail}, ph:${rspPhone}`
          : ''
      }
      Site: ${site}
      Language: ${language}
      WebForm UserAgent: ${userAgent}`;

    const data = {
      seekId: userId,
      accountNumber: billingId,
      additionalInformation,
      ...otherValues,
      siteCountry: locale,
      recaptcha,
    };

    if (environment === 'development') {
      logHandler([`Form data:`, data]);
      setSubmitting(false);
      setInvalidResponse(false);
      setSubmitted(true);
    } else {
      fetch(postUrl, {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then((response) => {
          if (response.status === 200) {
            trackEvent(TRACKING_CODES.content_element_pressed, {
              elementDesign: 'button',
              elementText: 'submit',
            });
            resetForm();

            setSubmitting(false);
            setInvalidResponse(false);
            setSubmitted(true);
          } else {
            setSubmitting(false);
            setInvalidResponse(true);
            throw response;
          }
        })
        .catch(() => {
          setSubmitting(false);
          setInvalidResponse(true);
        });
    }
  });

  const {
    subject,
    reason,
    firstName,
    lastName,
    email,
    company,
    additionalInformation,
  } = useFields({ isANZ });

  const subjectList = React.useMemo(
    () => Object.keys(subjectData),
    [subjectData],
  );

  const handleSubjectChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const subjectValue = event.currentTarget.value;
    setFieldValue('subject', subjectValue);
    setFieldValue('reason', '');
    setSubItems([]);
    reason.errorMessage = '';
    setReasonList(
      subjectData[subjectValue]?.filter((reasonItem) =>
        reasonItem.rspToggleRequired && rspEnabled === 'false'
          ? !reasonItem.rspToggleRequired
          : reasonItem,
      ),
      // .map((item) => item.reason),
    );
  };

  const handleReasonChange = (event: React.FormEvent<HTMLSelectElement>) => {
    // const reasonValue = event.currentTarget.value;
    const reasonData = reasonList.find(
      (item) => item.reason === event.currentTarget.value,
    ) as Reason;

    const {
      reason: reasonName,
      suggestion,
      subItems: subItemsData,
    } = reasonData;

    setFieldValue('reason', reasonName);

    if (subItemsData && subItemsData.length > 0) {
      setSubItems(subItemsData || undefined);
    } else {
      setSubItems(undefined);
    }

    if (suggestion) {
      setArticleSuggestion(() => suggestion);
    } else {
      setArticleSuggestion(undefined);
    }
  };

  const renderSubItems = () => (
    <Spread space="medium" direction="vertical">
      {subItems?.map((item: SubItem) => (
        <RenderField field={item} key={item.name} useField={useField} />
      ))}
    </Spread>
  );

  useEffect(() => {
    setReasonList(
      subjectData[subject.value]?.filter((reasonItem) =>
        reasonItem.rspToggleRequired && rspEnabled === 'false'
          ? !reasonItem.rspToggleRequired
          : reasonItem,
      ),
    );
  }, [querystring]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set the default value for the reason field since it is mandatory field in wpt-contact-api for APAC since Reason Field is disabled in APAC
  if (!isANZ) setFieldValue('reason', '-');

  return (
    <form onSubmit={onSubmit}>
      <Spread space="large" direction="vertical">
        <TextField
          id="firstName"
          label={t('First name')}
          message={!firstName.valid && firstName.errorMessage}
          onClear={() => setFieldValue('firstName', '')}
          tone={!firstName.valid ? 'critical' : 'neutral'}
          {...firstName}
        />
        <TextField
          id="lastName"
          label={t('Last name')}
          message={!lastName.valid && lastName.errorMessage}
          onClear={() => setFieldValue('lastName', '')}
          tone={!lastName.valid ? 'critical' : 'neutral'}
          {...lastName}
        />
        <TextField
          id="email"
          label={t('Email')}
          onClear={() => setFieldValue('email', '')}
          message={!email.valid && email.errorMessage}
          tone={!email.valid ? 'critical' : 'neutral'}
          {...email}
        />
        {!isANZ ? (
          <ContactNumber id="telephone" label={t('Contact number')} />
        ) : null}
        <TextField
          id="company"
          label={t('Company')}
          onClear={() => setFieldValue('company', '')}
          message={!company.valid && company.errorMessage}
          tone={!company.valid ? 'critical' : 'neutral'}
          {...company}
        />
        <Dropdown
          id="subject"
          label={t('Subject')}
          placeholder={t('Please select')}
          tone={!subject.valid ? 'critical' : 'neutral'}
          message={!subject.valid && subject.errorMessage}
          {...subject}
          onChange={(e) => handleSubjectChange(e)}
        >
          {subjectList &&
            subjectList.map((item, key) => (
              <option key={key} value={item}>
                {item}
              </option>
            ))}
        </Dropdown>
        {/* Only show the Reason field on ANZ */}
        {isANZ && (
          <>
            <Dropdown
              icon={<IconSubCategory />}
              disabled={subject.value === ''}
              id="reason"
              label={t('Reason')}
              placeholder={t('Please select')}
              tone={!reason.valid ? 'critical' : 'neutral'}
              message={!reason.valid && reason.errorMessage}
              {...reason}
              onChange={(e) => handleReasonChange(e)}
            >
              {reasonList?.map((item, key: number) => (
                <option key={key} value={item.reason}>
                  {item.reason}
                </option>
              ))}
            </Dropdown>
            {subItems && renderSubItems()}
          </>
        )}

        {ArticleSuggestion && (
          <ArticleSuggest tone="info" suggestion={ArticleSuggestion} />
        )}
        <Textarea
          id="additionalInformation"
          label={t('Additional Information')}
          characterLimit={1000}
          secondaryLabel={t('Max 1000 characters')}
          value={additionalInformation.value}
          onChange={additionalInformation.onChange}
          onBlur={additionalInformation.onBlur}
          message={
            !additionalInformation.valid && additionalInformation.errorMessage
          }
          tone={!additionalInformation.valid ? 'critical' : 'neutral'}
        />
        <Box className={styles.captchaContainer}>
          <ReCAPTCHA
            sitekey={
              environment === 'development'
                ? RECAPTCHA_KEY.dev
                : RECAPTCHA_KEY.prod
            }
            onChange={(val: any) => setRecaptcha(val)}
            hl={language.slice(0, 2)}
          />
        </Box>
        {showLegalPolicies && (
          <Box>
            {isANZ ? (
              <Text>
                By providing your personal information, you agree to the{' '}
                {createTextLink(`${candidateUrl}/collection-notice`)(
                  'Collection Notice',
                )}{' '}
                and{' '}
                {createTextLink(`${candidateUrl}/privacy`)('Privacy Policy')}.
              </Text>
            ) : (
              <Text>
                {t('Legal Policies', {
                  TextLink: createTextLink('/collection-notice'),
                  PrivacyTextLink: createTextLink('/privacy'),
                  brand,
                })}
              </Text>
            )}
          </Box>
        )}
        {invalidResponse && (
          <Alert tone="critical">
            <Text id="api-failure-text">
              {t(
                'There was a problem submitting your request. Please try again.',
              )}
            </Text>
          </Alert>
        )}
        <Box paddingTop="medium">
          <Actions>
            <Button tone="formAccent" type="submit" loading={submitting}>
              {submitting ? t('Sending') : t('Send')}
            </Button>
          </Actions>
        </Box>
        {environment !== 'production' && (
          <Box onClick={prefillForm(setFieldValue)} id="prefill-form">
            <Badge>Prefill form (testing only)</Badge>
          </Box>
        )}
      </Spread>
    </form>
  );
};

const prefillForm = (setFieldValue: FormProps['setFieldValue']) => () => {
  setFieldValue('firstName', 'TestFirstName');
  setFieldValue('lastName', 'TestLastName');
  setFieldValue('email', 'test@test.com');
  setFieldValue('company', 'TestCompany');
  setFieldValue('subject', 'Other');
  setFieldValue('reason', 'General feedback');
  setFieldValue('additionalInformation', 'Test only, please ignore');
};

export default Form;
